import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Calendar, Plus, Filter, ChevronLeft, ChevronRight, ExternalLink, Trash2, Check,
    Loader2, X,
} from "lucide-react";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

const PLATFORMS = [
    { key: "x", label: "X (Twitter)", color: "#0EA5E9" },
    { key: "linkedin", label: "LinkedIn", color: "#FFFFFF" },
    { key: "instagram", label: "Instagram", color: "#A3A3A3" },
    { key: "tiktok", label: "TikTok", color: "#CCCCCC" },
    { key: "blog_internal", label: "Blog (internal)", color: "#CCCCCC" },
    { key: "email_newsletter", label: "Email newsletter", color: "#A3A3A3" },
    { key: "other", label: "Other", color: "#CCCCCC" },
];

const STATUS_LABEL = {
    planned: "Planned",
    published_manual: "Published",
    skipped: "Skipped",
    draft: "Draft",
};

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
}
function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function fmtMonth(d) {
    return d.toLocaleString("en", { month: "long", year: "numeric" });
}
function ymd(d) {
    return d.toISOString().slice(0, 10);
}

function CalendarGrid({ month, entries, onDayClick, onEntryClick }) {
    const first = startOfMonth(month);
    const last = endOfMonth(month);
    const firstWeekday = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = last.getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    }
    while (cells.length % 7) cells.push(null);

    const byDay = useMemo(() => {
        const m = {};
        entries.forEach((e) => {
            const key = e.scheduled_at.slice(0, 10);
            (m[key] = m[key] || []).push(e);
        });
        return m;
    }, [entries]);

    return (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
             data-testid="schedule-calendar">
            <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                    <div key={d} className="text-center py-1">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, idx) => {
                    if (!d) return <div key={idx} className="min-h-[92px] rounded-md bg-white/[0.02]" />;
                    const key = ymd(d);
                    const items = byDay[key] || [];
                    const isToday = ymd(new Date()) === key;
                    return (
                        <button
                            key={idx}
                            onClick={() => onDayClick(d)}
                            data-testid={`schedule-day-${key}`}
                            className={`min-h-[92px] rounded-md border p-1.5 text-left overflow-hidden transition-colors ${
                                isToday
                                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                                    : "border-[var(--color-border)] hover:border-[var(--color-primary)]/40"
                            }`}
                        >
                            <div className="text-[11px] font-medium text-text-secondary mb-1">
                                {d.getDate()}
                            </div>
                            <div className="space-y-1">
                                {items.slice(0, 3).map((e) => (
                                    <div key={e.id}
                                        data-testid={`schedule-entry-${e.id}`}
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            onEntryClick(e);
                                        }}
                                        style={{ borderLeftColor: e.color_hex || "#CCCCCC" }}
                                        className="border-l-2 pl-1.5 py-0.5 rounded-r bg-[var(--color-panel-ai)] cursor-pointer hover:bg-white/5"
                                    >
                                        <div className="text-[10px] text-text-main truncate">
                                            {e.asset_title_snapshot}
                                        </div>
                                        <div className="text-[9px] text-text-secondary">
                                            {e.scheduled_at.slice(11, 16)} · {e.platform_label}
                                        </div>
                                    </div>
                                ))}
                                {items.length > 3 && (
                                    <div className="text-[9px] text-text-secondary text-center">
                                        +{items.length - 3} more
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function EntryModal({ open, onClose, entry, onSave, onDelete, onMarkPublished, defaultDate }) {
    const isNew = !entry?.id;
    const [form, setForm] = useState(() => ({
        asset_type: "blog_post",
        asset_id: "",
        platform_label: "linkedin",
        scheduled_at: defaultDate ? `${ymd(defaultDate)}T10:00` : "",
        notes: "",
        reminder_enabled: true,
    }));
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (entry?.id) {
            setForm({
                asset_type: entry.asset_type || "blog_post",
                asset_id: entry.asset_id || "",
                platform_label: entry.platform_label,
                scheduled_at: (entry.scheduled_at || "").slice(0, 16),
                notes: entry.notes || "",
                reminder_enabled: entry.reminder_enabled !== false,
            });
        } else if (defaultDate) {
            setForm((f) => ({ ...f, scheduled_at: `${ymd(defaultDate)}T10:00` }));
        }
    }, [entry, defaultDate]);
    if (!open) return null;

    const submit = async () => {
        setBusy(true);
        try {
            await onSave({
                ...form,
                scheduled_at: new Date(form.scheduled_at).toISOString(),
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-[var(--color-card)] border border-[var(--color-border)] p-6"
                 data-testid="schedule-modal">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-text-main">
                        {isNew ? "New schedule entry" : "Edit schedule entry"}
                    </h2>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-main">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                {!isNew && (
                    <div className="text-xs text-text-secondary mb-4">
                        Asset · {entry.asset_title_snapshot}
                    </div>
                )}
                <div className="grid gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Asset type</span>
                        <select value={form.asset_type}
                            onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
                            data-testid="schedule-asset-type"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main">
                            <option value="blog_post">Blog post</option>
                            <option value="marketplace_asset">Marketplace asset</option>
                            <option value="mini_landing">Mini-landing</option>
                            <option value="content_recycling">Recycled variant</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Asset ID</span>
                        <input value={form.asset_id}
                            onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
                            placeholder="UUID from admin lists"
                            data-testid="schedule-asset-id"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main font-mono" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Platform</span>
                        <select value={form.platform_label}
                            onChange={(e) => setForm({ ...form, platform_label: e.target.value })}
                            data-testid="schedule-platform"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main">
                            {PLATFORMS.map((p) => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Scheduled at (UTC)</span>
                        <input type="datetime-local" value={form.scheduled_at}
                            onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                            data-testid="schedule-datetime"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Notes</span>
                        <textarea value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            data-testid="schedule-notes"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main" />
                    </label>
                    <label className="flex items-center gap-2 text-xs text-text-secondary">
                        <input type="checkbox" checked={form.reminder_enabled}
                            onChange={(e) => setForm({ ...form, reminder_enabled: e.target.checked })}
                            data-testid="schedule-reminder" />
                        Send 15-min reminder before publish time
                    </label>
                </div>
                <p className="mt-3 text-[10px] text-text-secondary">
                    Global Lead Suite reminds you when to publish — you copy-paste manually on the target platform.
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                    {!isNew && (
                        <>
                            <button onClick={onDelete}
                                data-testid="schedule-delete"
                                className="mr-auto text-xs rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-2 flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Delete
                            </button>
                            <button onClick={onMarkPublished}
                                data-testid="schedule-mark-published"
                                className="text-xs rounded-full border border-[var(--color-success)]/40 text-[var(--color-success)] px-3 py-2 flex items-center gap-1">
                                <Check className="h-3 w-3" /> Mark published
                            </button>
                        </>
                    )}
                    <button onClick={onClose}
                        className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-2">
                        Cancel
                    </button>
                    <button onClick={submit} disabled={busy}
                        data-testid="schedule-save"
                        className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium flex items-center gap-1 disabled:opacity-50">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        {isNew ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminSchedule() {
    const { user: me } = useAuth();
    const [month, setMonth] = useState(startOfMonth(new Date()));
    const [entries, setEntries] = useState([]);
    const [platformFilter, setPlatformFilter] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [modalEntry, setModalEntry] = useState(null);
    const [defaultDate, setDefaultDate] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = {
                from: startOfMonth(month).toISOString(),
                to: endOfMonth(month).toISOString(),
            };
            if (platformFilter) params.platform = platformFilter;
            const r = await api.v30ScheduleList(params);
            setEntries(r.items || []);
        } catch (e) {
            toast.error(e.message || "Failed to load schedule");
        }
    }, [month, platformFilter]);
    useEffect(() => { load(); }, [load]);

    const openNewFor = (d) => {
        setModalEntry(null);
        setDefaultDate(d);
        setModalOpen(true);
    };
    const openEdit = (entry) => {
        setModalEntry(entry);
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
        setModalEntry(null);
        setDefaultDate(null);
    };

    const save = async (form) => {
        try {
            if (modalEntry?.id) {
                await api.v30ScheduleUpdate(modalEntry.id, form);
                toast.success("Updated");
            } else {
                await api.v30ScheduleCreate(form);
                toast.success("Scheduled");
            }
            closeModal();
            load();
        } catch (e) {
            toast.error(e.message || "Save failed");
        }
    };

    const del = async () => {
        if (!modalEntry?.id) return;
        if (!window.confirm("Delete this schedule entry?")) return;
        try {
            await api.v30ScheduleDelete(modalEntry.id);
            toast.success("Deleted");
            closeModal();
            load();
        } catch (e) { toast.error(e.message || "Delete failed"); }
    };

    const markPub = async () => {
        if (!modalEntry?.id) return;
        const url = window.prompt("External URL where you published it (optional):") || "";
        try {
            await api.v30ScheduleMarkPublished(modalEntry.id, url);
            toast.success("Marked as published");
            closeModal();
            load();
        } catch (e) { toast.error(e.message || "Failed"); }
    };

    if (!isAdminRole(me?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center">
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full space-y-4"
                        data-testid="admin-schedule">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Phase 10 · V30 · Scheduling
                            </div>
                            <h1 className="text-2xl md:text-3xl font-semibold text-text-main">Content schedule</h1>
                            <p className="text-xs text-text-secondary mt-1">
                                Plan your publishes. Global Lead Suite reminds you 15 min before — <strong>you copy-paste manually</strong> on the target platform.
                            </p>
                        </div>
                        <button
                            onClick={() => openNewFor(new Date())}
                            data-testid="schedule-new"
                            className="text-sm rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" /> New entry
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <button onClick={() => setMonth(addMonths(month, -1))}
                                data-testid="schedule-prev-month"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main p-2">
                                <ChevronLeft className="h-3 w-3" />
                            </button>
                            <div className="text-sm font-medium text-text-main w-40 text-center">
                                {fmtMonth(month)}
                            </div>
                            <button onClick={() => setMonth(addMonths(month, 1))}
                                data-testid="schedule-next-month"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main p-2">
                                <ChevronRight className="h-3 w-3" />
                            </button>
                            <button onClick={() => setMonth(startOfMonth(new Date()))}
                                data-testid="schedule-today"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5">
                                Today
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="h-3 w-3 text-text-secondary" />
                            <select value={platformFilter}
                                onChange={(e) => setPlatformFilter(e.target.value)}
                                data-testid="schedule-platform-filter"
                                className="px-3 py-1.5 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-xs text-text-main">
                                <option value="">All platforms</option>
                                {PLATFORMS.map((p) => (
                                    <option key={p.key} value={p.key}>{p.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <CalendarGrid
                        month={month}
                        entries={entries}
                        onDayClick={openNewFor}
                        onEntryClick={openEdit}
                    />

                    <div className="flex flex-wrap gap-2 text-[10px] text-text-secondary">
                        {PLATFORMS.map((p) => (
                            <span key={p.key} className="flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded"
                                        style={{ backgroundColor: p.color }} />
                                {p.label}
                            </span>
                        ))}
                    </div>
                </main>
            </div>
            <EntryModal
                open={modalOpen}
                onClose={closeModal}
                entry={modalEntry}
                defaultDate={defaultDate}
                onSave={save}
                onDelete={del}
                onMarkPublished={markPub}
            />
        </div>
    );
}
